"use client"

import { startTransition, useCallback, useMemo } from "react"
/*import Link from "next/link"*/
import { useRouter } from "next/navigation"
import { useUbStorefrontState } from "@/store/ubStorefrontState"

import { FacetElementData } from "@/lib/factfinder/factfinder-interfaces"
import { getLinkWithEncryptedFfSearchParams } from "@/lib/link-utils/link-utils"
import { Category } from "@/lib/storefront/category/category"
import { formatFacetElementLabel } from "@/components/search-and-category/facet-label"

interface FacetElementProps {
  facetElementData: FacetElementData
  query?: string
  baseCategory?: Category
  brandSlug?: string
  unit?: string
  sbLocale?: string
  isoLocale: string
  link?: string
}

function FacetElementClientLink(props: FacetElementProps) {
  const hitcount = props.facetElementData.hitcount
  const elementname = props.facetElementData.elementname
  const selected = props.facetElementData.selected
  const link = props.link

  const { setIsLoading } = useUbStorefrontState()

  const handleSetLoading = useCallback(() => {
    setIsLoading(true)
  }, [setIsLoading])

  return (
    <a className={"facetMarker"} href={link} onClick={handleSetLoading}>
      <div className={"flex justify-between"}>
        <div
          className="
            flex min-w-40 flex-row items-center gap-1 transition duration-200 ease-in-out
            hover:translate-x-1 hover:text-primary
          "
        >
          <input
            aria-label={"filter_" + elementname}
            //Diese beiden Eigenschaften verhindern events an der checkbox
            className={"pointer-events-none checked:bg-primary hover:checked:bg-primary focus:ring-0"}
            tabIndex={-1} // Verhindert, dass die Checkbox per Tastatur fokussiert wird
            name={"filter_" + elementname}
            type="checkbox"
            // WICHTIG: `checked` statt `defaultChecked` verwenden
            // `checked` stellt sicher, dass die UI immer den `selected`-Prop widerspiegelt.
            checked={selected}
            readOnly // Zeigt an, dass der Wert nicht direkt vom Benutzer geändert werden kann
          />

          <span>{formatFacetElementLabel(elementname, props.unit)}</span>
        </div>
        <p
          className="
            max-h-[1.35rem] rounded-sm border border-gray-300 px-[0.2rem] text-[0.6rem] font-bold text-gray-700
          "
        >
          {hitcount}
        </p>
      </div>
    </a>
  )
}

function FacetElementClientCheckbox(props: FacetElementProps) {
  const hitcount = props.facetElementData.hitcount
  const elementname = props.facetElementData.elementname
  const selected = props.facetElementData.selected
  const router = useRouter()

  const link = props.link ?? ""

  const { setIsLoading } = useUbStorefrontState()

  const handleSetLoading = useCallback(() => {
    setIsLoading(true)
  }, [setIsLoading])

  const handleChange = useCallback(
    (e) => {
      setIsLoading(true)
      e.preventDefault()
      startTransition(() => {
        router.push(link)
      })
    },
    [link, router, setIsLoading]
  )

  return (
    <label htmlFor={"filter_" + elementname} className="flex cursor-pointer justify-between">
      <div
        className="
          flex min-w-40 flex-row items-center gap-1 transition duration-200 ease-in-out
          hover:translate-x-1 hover:text-primary
        "
      >
        <input
          id={"filter_" + elementname} // ID ist für das Label `htmlFor` nötig
          aria-label={"filter_" + elementname}
          className="checked:bg-primary hover:checked:bg-primary focus:ring-0"
          name={"filter_" + elementname}
          type="checkbox"
          checked={selected}
          onChange={handleChange}
        />
        <span>{formatFacetElementLabel(elementname, props.unit)}</span>
      </div>
      <p className="max-h-[1.35rem] rounded-sm border border-gray-300 px-[0.2rem] text-[0.6rem] font-bold text-gray-700">
        {hitcount}
      </p>
    </label>
  )
}

export default function FacetElementClient(props: FacetElementProps) {
  const link = useMemo(() => {
    return getLinkWithEncryptedFfSearchParams(
      props.query,
      props.baseCategory,
      props.brandSlug,
      props.facetElementData.encryptedFfSearchParams,
      props.sbLocale
    )
  }, [
    props.baseCategory,
    props.brandSlug,
    props.facetElementData.encryptedFfSearchParams,
    props.query,
    props.sbLocale,
  ])

  return (
    <FacetElementClientLink
      baseCategory={props.baseCategory}
      brandSlug={props.brandSlug}
      facetElementData={props.facetElementData}
      isoLocale={props.isoLocale}
      sbLocale={props.sbLocale}
      unit={props.unit}
      link={link}
    />
  )
}
